import React, { useState, useEffect } from 'react';
import { X, Snowflake, Clock, AlertTriangle, LayoutGrid, Monitor, Timer } from 'lucide-react';
import { Button } from './ui/button';

const VERSION = 'snowman-1';

const CHANGES = [
  {
    icon: Timer,
    title: 'Per-Slide Timing',
    description: 'Set custom durations for each individual slide — from 3 seconds to 2 minutes. No more one-size-fits-all.',
    color: 'text-cyan-400',
  },
  {
    icon: AlertTriangle,
    title: 'Announcement Bar',
    description: 'Push urgent scrolling messages to any display instantly. Perfect for closures, events, or alerts.',
    color: 'text-amber-400',
  },
  {
    icon: LayoutGrid,
    title: 'Multi-Zone Layout',
    description: 'Split your screen into zones: main slideshow + clock/date sidebar + scrolling bottom ticker.',
    color: 'text-emerald-400',
  },
  {
    icon: Monitor,
    title: 'Live Preview',
    description: 'See exactly what your display will show — right in the admin panel. Play, pause, and skip through slides.',
    color: 'text-violet-400',
  },
  {
    icon: Clock,
    title: 'Full Admin Controls on All Pages',
    description: 'Every page you create now gets the complete set of controls: orientation, playlists, passwords, kiosk remote, and more.',
    color: 'text-rose-400',
  },
];

const WhatsNew = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(`whats-new-${VERSION}`);
    if (!seen) {
      const timer = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(`whats-new-${VERSION}`, 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="whats-new-modal">
      <div
        className="relative w-full max-w-lg mx-4 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-900 via-cyan-950/30 to-slate-900 px-8 pt-8 pb-6">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
            data-testid="whats-new-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Snowflake className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold tracking-tight">What's New</h2>
              <p className="text-cyan-400 text-sm font-medium">Snowman 1</p>
            </div>
          </div>
          <p className="text-slate-400 text-sm">
            FrostByte Signage just got a major upgrade. Here's everything new:
          </p>
        </div>

        {/* Changes list */}
        <div className="px-8 py-5 space-y-4 max-h-[50vh] overflow-y-auto">
          {CHANGES.map((change, i) => (
            <div key={i} className="flex gap-3 group">
              <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center border border-slate-800 group-hover:border-slate-700 transition-colors`}>
                <change.icon className={`w-4 h-4 ${change.color}`} />
              </div>
              <div>
                <h3 className="text-white text-sm font-semibold">{change.title}</h3>
                <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{change.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-8 pb-6 pt-2">
          <Button
            onClick={dismiss}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-semibold"
            data-testid="whats-new-dismiss"
          >
            Got it, let's go
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNew;
